/**
 * Enhanced Event Tracking Hook
 *
 * This hook provides pre-configured tracking functions for all major site actions,
 * automatically enriching events with maximum possible data similar to the redirect page approach.
 */

import { useCallback } from 'react';
import { useEventTracking } from './useEventTracking';
import {
  trackJobView,
  trackJobApply,
  trackSearch,
  trackProfileUpdate,
  trackUnsubscribe,
  trackEnhancedClick,
  trackFormSubmit,
  createEnhancedEventData,
} from '@/utils/eventTrackingHelpers';
import { EventType, EventData } from '@/api/types/events';

/**
 * Enhanced tracking hook that provides easy-to-use tracking functions
 * with comprehensive data collection similar to the redirect page implementation
 */
export const useEnhancedTracking = () => {
  const { trackEvent } = useEventTracking();

  // Job-related tracking
  const trackJobViewEvent = useCallback((
    jobData: {
      jobId: string;
      jobTitle?: string;
      jobCompany?: string;
      jobLocation?: string;
    },
    context?: {
      componentName?: string;
      pageSection?: string;
      [key: string]: any;
    }
  ) => {
    trackJobView(trackEvent, jobData, context);
  }, [trackEvent]);

  const trackJobApplyEvent = useCallback((
    applicationData: {
      jobId: string;
      jobTitle?: string;
      jobCompany?: string;
      jobLocation?: string;
    },
    context?: {
      componentName?: string;
      pageSection?: string;
      [key: string]: any;
    }
  ) => {
    trackJobApply(trackEvent, applicationData, context);
  }, [trackEvent]);

  // Search tracking
  const trackSearchEvent = useCallback((
    searchData: {
      searchTerm: string;
      searchType?: string;
      resultsCount?: number;
      filters?: Record<string, any>;
      sortBy?: string;
      location?: string;
    },
    context?: {
      componentName?: string;
      pageSection?: string;
      [key: string]: any;
    }
  ) => {
    trackSearch(trackEvent, searchData, context);
  }, [trackEvent]);

  // Profile update tracking
  const trackProfileUpdateEvent = useCallback((
    updateData: {
      section: string;
      fieldsUpdated: string[];
      updateType?: string;
      profileCompleteness?: number;
    },
    context?: {
      componentName?: string;
      pageSection?: string;
      [key: string]: any;
    }
  ) => {
    trackProfileUpdate(trackEvent, updateData, context);
  }, [trackEvent]);

  // Unsubscribe tracking
  const trackUnsubscribeEvent = useCallback((
    unsubscribeData: {
      emailType?: string;
      reason?: string;
      campaignId?: number;
      mailId?: string;
      userId?: string;
    },
    context?: {
      componentName?: string;
      pageSection?: string;
      [key: string]: any;
    }
  ) => {
    trackUnsubscribe(trackEvent, unsubscribeData, context);
  }, [trackEvent]);

  // Enhanced click tracking
  const trackClickEvent = useCallback((
    clickData: {
      elementId?: string;
      elementClass?: string;
      elementText?: string;
      linkUrl?: string;
      buttonType?: string;
    },
    context?: {
      componentName?: string;
      pageSection?: string;
      [key: string]: any;
    }
  ) => {
    trackEnhancedClick(trackEvent, clickData, context);
  }, [trackEvent]);

  // Form submission tracking
  const trackFormSubmitEvent = useCallback((
    formData: {
      formName: string;
      formFields?: string[];
      formAction?: string;
      validationErrors?: string[];
      completionTime?: number;
    },
    context?: {
      componentName?: string;
      pageSection?: string;
      [key: string]: any;
    }
  ) => {
    trackFormSubmit(trackEvent, formData, context);
  }, [trackEvent]);

  // Error tracking
  const trackError = useCallback((
    errorData: {
      errorType: string;
      errorCode?: string | number;
      errorMessage?: string;
      errorStack?: string;
      context?: string;
      componentName?: string;
      userId?: string;
      additionalData?: Record<string, any>;
    }
  ) => {
    const eventData = createEnhancedEventData(EventType.ERROR, {
      user_id: errorData.userId,
    }, {
      error: {
        code: errorData.errorCode?.toString(),
        message: errorData.errorMessage,
        stack: errorData.errorStack,
      },
      componentName: errorData.componentName,
      pageSection: errorData.context,
    });

    eventData.event_details = {
      ...eventData.event_details,
      error_type: errorData.errorType,
      ...errorData.additionalData,
    };

    trackEvent(eventData);
  }, [trackEvent]);

  // Performance tracking
  const trackPerformance = useCallback((
    performanceData: {
      metricName: string;
      metricValue: number;
      metricUnit?: string;
      context?: string;
      componentName?: string;
      additionalData?: Record<string, any>;
    }
  ) => {
    const eventData = createEnhancedEventData(EventType.PERFORMANCE_METRIC, {
      interaction_value: performanceData.metricValue,
      element_text: performanceData.metricName,
    }, {
      componentName: performanceData.componentName,
      pageSection: performanceData.context,
    });

    eventData.event_details = {
      ...eventData.event_details,
      metric_name: performanceData.metricName,
      metric_value: performanceData.metricValue,
      metric_unit: performanceData.metricUnit,
      ...performanceData.additionalData,
    };

    trackEvent(eventData);
  }, [trackEvent]);

  // Conversion tracking
  const trackConversion = useCallback((
    conversionData: {
      conversionType: string;
      conversionValue?: number;
      conversionCurrency?: string;
      conversionId?: string;
      campaignId?: number;
      additionalData?: Record<string, any>;
    }
  ) => {
    const eventData = createEnhancedEventData(EventType.CONVERSION, {
      interaction_value: conversionData.conversionValue,
      campaign_id: conversionData.campaignId,
      type: conversionData.conversionType,
    }, {
      campaignId: conversionData.campaignId,
    });

    eventData.event_details = {
      ...eventData.event_details,
      conversion_type: conversionData.conversionType,
      conversion_value: conversionData.conversionValue,
      conversion_currency: conversionData.conversionCurrency,
      conversion_id: conversionData.conversionId,
      ...conversionData.additionalData,
    };

    trackEvent(eventData);
  }, [trackEvent]);

  // Feature toggle tracking
  const trackFeatureToggle = useCallback((
    featureData: {
      featureName: string;
      isEnabled: boolean;
      userId?: string;
      experimentId?: string;
      variantId?: string;
      additionalData?: Record<string, any>;
    }
  ) => {
    const eventData = createEnhancedEventData(EventType.FEATURE_TOGGLE, {
      user_id: featureData.userId,
      element_text: featureData.featureName,
      interaction_value: featureData.isEnabled ? 1 : 0,
    }, {
      featureFlag: featureData.featureName,
      experimentVariant: featureData.variantId,
    });

    eventData.event_details = {
      ...eventData.event_details,
      feature_name: featureData.featureName,
      is_enabled: featureData.isEnabled,
      experiment_id: featureData.experimentId,
      variant_id: featureData.variantId,
      ...featureData.additionalData,
    };

    trackEvent(eventData);
  }, [trackEvent]);

  // Navigation tracking
  const trackNavigation = useCallback((
    navigationData: {
      from?: string;
      to: string;
      navigationType?: 'link' | 'button' | 'programmatic' | 'back' | 'forward';
      elementId?: string;
      elementText?: string;
      additionalData?: Record<string, any>;
    }
  ) => {
    const eventData = createEnhancedEventData(EventType.NAVIGATION, {
      element_id: navigationData.elementId,
      element_text: navigationData.elementText,
      referrer: navigationData.from,
      url: navigationData.to,
      type: navigationData.navigationType,
    });

    eventData.event_details = {
      ...eventData.event_details,
      navigation_from: navigationData.from,
      navigation_to: navigationData.to,
      navigation_type: navigationData.navigationType,
      ...navigationData.additionalData,
    };

    trackEvent(eventData);
  }, [trackEvent]);

  // Generic enhanced event tracking (for custom events)
  const trackCustomEvent = useCallback((
    eventType: EventType,
    eventData?: Partial<EventData>,
    enhancedData?: Record<string, any>
  ) => {
    const enrichedEventData = createEnhancedEventData(eventType, eventData, enhancedData);
    trackEvent(enrichedEventData);
  }, [trackEvent]);

  return {
    // Specific tracking functions
    trackJobView: trackJobViewEvent,
    trackJobApply: trackJobApplyEvent,
    trackSearch: trackSearchEvent,
    trackProfileUpdate: trackProfileUpdateEvent,
    trackUnsubscribe: trackUnsubscribeEvent,
    trackClick: trackClickEvent,
    trackFormSubmit: trackFormSubmitEvent,

    // System tracking functions
    trackError,
    trackPerformance,
    trackConversion,
    trackFeatureToggle,
    trackNavigation,

    // Generic tracking
    trackCustomEvent,

    // Direct access to base tracking (for edge cases)
    trackEvent,
  };
};